/**
 * Sazook - Reactive State Store (TypeScript)
 * Supports Multi-Item Cart & Battery Addon
 */

import { ProductSize, ProductColor, CartItem } from '../types';

export interface AppState {
  sizes: Record<string, ProductSize>;
  colors: Record<string, ProductColor>;
  variants: Record<string, Record<string, string>>;
  variantStock: Record<string, Record<string, boolean>>;
  selectedSize: string;
  selectedColor: string;
  quantity: number;
  cartItems: CartItem[];
  hasBattery: boolean;
  batteryPrice: number;
  batteryQuantity: number;
  deliveryCharge: number;
  deliveryRates: { insideDhaka: number; outsideDhaka: number };
  whatsappNumber: string;
  userUuid: string;
  userIp: string;
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
  userOs: string;
  isBlocked: boolean;
  blockedIps: string[];
}

export class StateStore {
  private static instance: StateStore;
  
  public state: AppState = {
    sizes: {
      '6ft': {
        id: '6ft',
        name: '৬ ফিট (৭২ ইঞ্চি)',
        price: 390,
        oldPrice: 650,
        savings: 260,
        inStock: true
      },
      '12ft': {
        id: '12ft',
        name: '১২ ফিট (১৪৪ ইঞ্চি)',
        price: 690,
        oldPrice: 1200,
        savings: 510,
        inStock: true
      }
    },
    colors: {
      'light-pink': {
        id: 'light-pink',
        name: 'Light Pink (হালকা গোলাপি)',
        image: 'assets/images/6ft-light-pink.webp',
        hex: '#ffb3c6',
        inStock: true
      },
      'deep-pink': {
        id: 'deep-pink',
        name: 'Deep Pink (গাঢ় গোলাপি)',
        image: 'assets/images/6ft-deep-pink.webp',
        hex: '#ff4d6d',
        inStock: true
      },
      'white': {
        id: 'white',
        name: 'White (স্নো হোয়াইট)',
        image: 'assets/images/6ft-white.webp',
        hex: '#ffffff',
        inStock: true
      },
      'peach': {
        id: 'peach',
        name: 'Peach (পীচ কালার)',
        image: 'assets/images/6ft-peach.webp',
        hex: '#ffcaa7',
        inStock: true
      },
      'purple': {
        id: 'purple',
        name: 'Purple (রাউণ্ড পার্পল)',
        image: 'assets/images/6ft-purple.webp',
        hex: '#c77dff',
        inStock: true
      }
    },
    variants: {
      '6ft': {
        'light-pink': 'assets/images/6ft-light-pink.webp',
        'deep-pink': 'assets/images/6ft-deep-pink.webp',
        'white': 'assets/images/6ft-white.webp',
        'peach': 'assets/images/6ft-peach.webp',
        'purple': 'assets/images/6ft-purple.webp'
      },
      '12ft': {
        'light-pink': 'assets/images/12ft-light-pink.webp',
        'deep-pink': 'assets/images/12ft-deep-pink.webp',
        'white': 'assets/images/12ft-white.webp',
        'peach': 'assets/images/12ft-peach.webp',
        'purple': 'assets/images/12ft-purple.webp'
      }
    },
    variantStock: {
      '12ft': {
        'light-pink': true,
        'deep-pink': true,
        'white': true,
        'peach': true,
        'purple': true
      },
      '6ft': {
        'light-pink': true,
        'deep-pink': true,
        'white': true,
        'peach': true,
        'purple': true
      }
    },
    selectedSize: '6ft',
    selectedColor: 'light-pink',
    quantity: 1,
    cartItems: [
      {
        id: 'item-1',
        size: '6ft',
        color: 'light-pink',
        quantity: 1,
        price: 390
      }
    ],
    hasBattery: false,
    batteryPrice: 60,
    batteryQuantity: 1,
    deliveryCharge: 70,
    deliveryRates: { insideDhaka: 70, outsideDhaka: 130 },
    whatsappNumber: '01303209784',
    userUuid: '',
    userIp: '',
    deviceType: 'Desktop',
    userOs: 'Unknown',
    isBlocked: false,
    blockedIps: []
  };

  public static getInstance(): StateStore {
    if (!StateStore.instance) {
      StateStore.instance = new StateStore();
    }
    return StateStore.instance;
  }

  public isVariantInStock(sizeKey?: string, colorKey?: string): boolean {
    const s = sizeKey || this.state.selectedSize;
    const c = colorKey || this.state.selectedColor;
    if (this.state.variantStock && this.state.variantStock[s] && this.state.variantStock[s][c] !== undefined) {
      return this.state.variantStock[s][c] === true;
    }
    if (this.state.sizes[s] && this.state.sizes[s].inStock === false) {
      return false;
    }
    if (this.state.colors[c] && this.state.colors[c].inStock === false) {
      return false;
    }
    return true;
  }

  public get currentSize(): ProductSize {
    const keys = Object.keys(this.state.sizes || {});
    return this.state.sizes?.[this.state.selectedSize] 
      || (keys.length > 0 ? this.state.sizes[keys[0]] : null) 
      || { id: 'default', name: 'স্ট্যান্ডার্ড সাইজ', price: 690, oldPrice: 790, savings: 100, inStock: true };
  }

  public get currentColor(): ProductColor {
    const keys = Object.keys(this.state.colors || {});
    return this.state.colors?.[this.state.selectedColor] 
      || (keys.length > 0 ? this.state.colors[keys[0]] : null) 
      || { id: 'default', name: 'স্ট্যান্ডার্ড কালার', hex: '#ffb3c6', image: '', inStock: true };
  }

  public get currentImage(): string {
    const size = this.state.selectedSize;
    const color = this.state.selectedColor;
    
    const isUploaded = (url?: string): boolean => !!(url && (url.includes('assets/images/uploads/') || url.startsWith('data:') || url.startsWith('http') || url.includes('assets/images/')));
    const variantImg = this.state.variants?.[size]?.[color];

    if (isUploaded(variantImg)) {
      return variantImg!;
    }

    const colorImg = this.state.colors?.[color]?.image;
    if (isUploaded(colorImg)) {
      return colorImg;
    }

    if (this.state.variants) {
      for (const sKey of Object.keys(this.state.variants)) {
        const otherImg = this.state.variants[sKey]?.[color];
        if (isUploaded(otherImg)) return otherImg;
      }
    }

    return variantImg || colorImg || 'assets/images/12ft-light-pink.webp';
  }

  public get totalItemCount(): number {
    if (this.state.cartItems && this.state.cartItems.length > 0) {
      return this.state.cartItems.reduce((acc, it) => acc + (it.quantity || 1), 0);
    }
    return this.state.quantity || 1;
  }

  public get freeDeliveryThreshold(): number {
    return 1000;
  }

  public get isFreeDelivery(): boolean {
    return this.subtotal >= this.freeDeliveryThreshold;
  }

  public get remainingForFreeDelivery(): number {
    return Math.max(0, this.freeDeliveryThreshold - this.subtotal);
  }

  public get freeDeliveryProgressPercent(): number {
    if (this.isFreeDelivery) return 100;
    return Math.min(100, Math.max(5, Math.round((this.subtotal / this.freeDeliveryThreshold) * 100)));
  }

  public get effectiveDeliveryCharge(): number {
    return this.isFreeDelivery ? 0 : this.state.deliveryCharge;
  }

  public get subtotal(): number {
    if (this.state.cartItems && this.state.cartItems.length > 0) {
      return this.state.cartItems.reduce((acc, it) => acc + (it.price * it.quantity), 0);
    }
    return this.currentSize.price * this.state.quantity;
  }

  public get batteryTotal(): number {
    return this.state.hasBattery ? (this.state.batteryPrice * (this.state.batteryQuantity || 1)) : 0;
  }

  public get grandTotal(): number {
    return this.subtotal + this.batteryTotal + this.effectiveDeliveryCharge;
  }
}
